// vim: tabstop=8 softtabstop=0 noexpandtab shiftwidth=8 nosmarttab

import * as oauth2 from 'oauth4webapi';
import { decodeJwt } from 'jose';
import { TokenSet } from "./token-set.js";
import { TOKEN_EXPIRATION_LEEWAY } from './config.js';

export interface DeviceFlowHandleLike {
	as: oauth2.AuthorizationServer;
	client: oauth2.Client;
	client_auth: oauth2.ClientAuth;
	device_code: string;
	user_code: string;
	verification_uri: string;
	verification_uri_complete?: string;
	expires_in: number;
	interval?: number;
};

export interface PollSettings {
};

export class DeviceFlowHandle {
	as: oauth2.AuthorizationServer;
	client: oauth2.Client;
	client_auth: oauth2.ClientAuth;
	device_code: string;
	user_code: string;
	verification_uri: string;
	verification_uri_complete?: string;
	expires_in: number;
	interval: number;
	expires_at: number;
	aborted: boolean;
	_expired = false;

	constructor(options: DeviceFlowHandleLike) {
		this.as = options.as;
		this.client = options.client;
		this.client_auth = options.client_auth;
		this.device_code = options.device_code;
		this.user_code = options.user_code;
		this.verification_uri = options.verification_uri;
		this.verification_uri_complete = options.verification_uri_complete;
		this.expires_in = options.expires_in;
		this.interval = options.interval || 5;

		this.expires_at = Date.now() + (this.expires_in * 1000);
		this.aborted = false;
	}

	// HTTP/403 Forbidden
	// {
	//   "error": "authorization_pending",
	//   "error_description": "User has yet to authorize device code."
	// }
	//
	// HTTP/200 OK
	// {
	//   "access_token": "eyJh...",
	//   "refresh_token": "V4sz...",
	//   "id_token": "eyJh...",
	//   "scope": "openid api:read api:write offline_access",
	//   "expires_in": 86400,
	//   "token_type": "Bearer"
	// }
	async poll(options: PollSettings = {}): Promise<TokenSet> {
		if(this.aborted) {
			throw new Error('aborted');
		}
		if(this.expired()) {
			throw new Error('expired');
		}
		await new Promise((resolve) => setTimeout(resolve, this.interval * 1000));
		const response = await oauth2.deviceCodeGrantRequest(
			this.as,
			this.client,
			this.client_auth,
			this.device_code,
		);
		let success: oauth2.TokenEndpointResponse | undefined = undefined
		try {
			success = await oauth2.processDeviceCodeResponse(
				this.as,
				this.client,
				response,
			);
			if(typeof success === 'undefined') {
				return this.poll(options);
			}
		} catch (e) {
			if(e instanceof oauth2.ResponseBodyError) {
				switch(e.error) {
				// @ts-ignore fallthough
				case 'slow_down':
					this.interval += 5;
				case 'authorization_pending':
					return this.poll(options);
				case 'expired_token':
					this._expired = true;
					throw new Error('expired');
				default:
					console.log('error', e.error);
					throw new Error('OAuth2Error'); // Handle OAuth 2.0 response body error
				}
			}
			throw e; // Handle other errors
		}
		if(typeof success.access_token !== 'string') {
			throw new Error('no access token');
		}
		if(typeof success.refresh_token !== 'string') {
			throw new Error('no refresh token');
		}
		console.log('result:', success);
		let expiresAt = 0;
		const jwtPayload = decodeJwt(success.access_token);
		if(jwtPayload
			&& typeof jwtPayload.exp === 'number')
		{
			expiresAt = jwtPayload.exp * 1000;
		}
		const tokenSet = new TokenSet({
			access_token: success.access_token,
			refresh_token: success.refresh_token,
			expires_at: expiresAt,
		});
		return tokenSet;
	}

  	abort(): void {
		this.aborted = true;
	}

  	expired(): boolean {
		const expired = Date.now() > (this.expires_at + TOKEN_EXPIRATION_LEEWAY);
		this._expired = expired;
		return expired;
	}
}
